import {
  ICreateSignatureRequest,
  IRejectSignatureRequest,
  IRejectSignatureResponse,
  ISignatureResponse,
} from '@literax/models/signature.model';
import {
  IShowSignatureResponse,
  SingRequestForm,
} from '@literax/models/http/api/signature.model';
import { createAction, props } from '@ngrx/store';

import { DocumentSignature } from '@literax/models/document-signature';
import { HttpErrorResponse } from '@angular/common/http';
import { ITemplates } from '@literax/models/templates';

export enum ESignatureActions {
  CreateSignature = '[Signature] Create Signature',
  CreateSignatureSuccess = '[Signature] Create Signature Success',
  CreateSignatureError = '[Signature] Create Signature Error',
  CreateSimpleSignature = '[Signature] Create Simple Signature',
  CreateSimpleSignatureSuccess = '[Signature] Create Simple Signature Success',
  CreateSimpleSignatureError = '[Signature] Create Simple Signature Error',
  ClearSignatureResult = '[Signature] Clear Signature Result',
  ShowSignature = '[Signature] Show Signature',
  ShowSignatureSuccess = '[Signature] Show Signature Success',
  ShowSignatureError = '[Signature] Show Signature Error',
  twoFactorToken = '[Signature] thoFactorToken',
  twoFactorTokenSuccess = '[Signature] twoFactorTokenSuccess',
  twoFactorTokenError = '[Signature] twoFactorTokenError',
  confirmTwoFactorToken = '[Signature] confirmTwoFactorToken',
  confirmTwoFactorTokenSuccess = '[Signature] confirmTwoFactorTokenSuccess',
  confirmTwoFactorTokenError = '[Signature] confirmTwoFactorTokenError',
  loadTwoFactorTokenStatus = '[Signature] loadTwoFactorTokenStatus',
  loadTwoFactorTokenStatusSuccess = '[Signature] loadTwoFactorTokenStatusSuccess',
  loadTwoFactorTokenStatusError = '[Signature] loadTwoFactorTokenStatusError',
  createSimpleSignature = '[Signature] Create Simple Signature',
  createSimpleSignatureSuccess = '[Signature] Create Simple Signature Success',
  createSimpleSignatureError = '[Signature] Create Simple Signature Error',

  GetSignatureQuote = '[Signature] Get signature quote by participant',
  GetSignatureQuoteSuccess = '[Signature] Get signature quote by participant success',
  GetSignatureQuoteError = '[Signature] Get signature quote by participant',
  ClearSignatureQuotes = '[Signature] Clear signature quote by participant',
}
export const CreateSimpleSignature = createAction(
  ESignatureActions.CreateSimpleSignature,
  props<{ payload: DocumentSignature }>()
);

export const CreateSignature = createAction(
  ESignatureActions.CreateSignature,
  props<{ payload: { documentId: number; params: SingRequestForm } }>()
);

export const CreateSignatureSuccess = createAction(
  ESignatureActions.CreateSignatureSuccess,
  props<{ payload: ISignatureResponse }>()
);
export const CreateSignatureError = createAction(
  ESignatureActions.CreateSignatureError,
  props<{ payload: HttpErrorResponse }>()
);
export const ClearSignatureResult = createAction(
  ESignatureActions.ClearSignatureResult
);
export const ShowSignature = createAction(
  ESignatureActions.ShowSignature,
  props<{ payload: string }>()
);
export const ShowSignatureSuccess = createAction(
  ESignatureActions.ShowSignatureSuccess,
  props<{ payload: IShowSignatureResponse }>()
);
export const ShowSignatureError = createAction(
  ESignatureActions.ShowSignatureError,
  props<{ payload: HttpErrorResponse }>()
);

/**
 * Requests (or resends) the code for the participant's current step.
 *
 * FR-039: `channel` is set only on a dual-channel document, where the verification type alone does
 * not say which channel the code goes out through — a resend on step 2 sends an SMS, and without
 * this the notification announced an email. Absent on every single-channel document, which keeps
 * choosing its wording from the verification type as before.
 */
export const twoFactorToken = createAction(
  ESignatureActions.twoFactorToken,
  props<{
    documentId: number;
    signRequestId: number;
    token?: string;
    channel?: 'email' | 'sms';
  }>()
);

export const twoFactorTokenSuccess = createAction(
  ESignatureActions.twoFactorTokenSuccess
);

export const twoFactorTokenError = createAction(
  ESignatureActions.twoFactorTokenError,
  props<{ payload: { message: string } }>()
);

/** Confirms the code entered for the sign request's current dual-channel step (FR-008). */
export const confirmTwoFactorToken = createAction(
  ESignatureActions.confirmTwoFactorToken,
  props<{
    documentId: number;
    signRequestId: number;
    confirmationToken: string;
    token?: string;
  }>()
);

/** The confirmation succeeded: which step was confirmed, what step is next (if any), and whether signing may proceed. */
export const confirmTwoFactorTokenSuccess = createAction(
  ESignatureActions.confirmTwoFactorTokenSuccess,
  props<{
    payload: {
      confirmedStep: number;
      nextStep: number | null;
      readyToSign: boolean;
    };
  }>()
);

/**
 * The confirmation was rejected (wrong code, expired, already confirmed, window elapsed, etc.).
 *
 * FR-035: `fields` and `httpStatus` travel alongside `message` because a `422` from the validator
 * carries the reason the signer needs under `fields.confirmationToken`, while `message` is only the
 * generic heading that accompanies it. Both are optional — a `400` raised by the service layer
 * carries a meaningful `message` and no `fields` at all.
 */
export const confirmTwoFactorTokenError = createAction(
  ESignatureActions.confirmTwoFactorTokenError,
  props<{
    payload: {
      message: string;
      fields?: Record<string, string[]>;
      httpStatus?: number;
    };
  }>()
);

/**
 * Reads the participant's real current dual-channel step on load (FR-015b, A-008) — resuming on a
 * fresh page load or a different device must reflect the backend's recorded progress instead of
 * defaulting to the email step.
 */
export const loadTwoFactorTokenStatus = createAction(
  ESignatureActions.loadTwoFactorTokenStatus,
  props<{ documentId: number; signRequestId: number; token?: string }>()
);

/** The participant's real current step and whether both steps are already satisfied. */
export const loadTwoFactorTokenStatusSuccess = createAction(
  ESignatureActions.loadTwoFactorTokenStatusSuccess,
  props<{ payload: { currentStep: number; readyToSign: boolean } }>()
);

/** The status read failed; the form keeps its safe email/step-1 default. */
export const loadTwoFactorTokenStatusError = createAction(
  ESignatureActions.loadTwoFactorTokenStatusError,
  props<{ payload: { message: string } }>()
);

// ! Deprecated: change action for registerSignature
export const createSimpleSignature = createAction(
  ESignatureActions.createSimpleSignature,
  props<{ payload: { signature: DocumentSignature; document_id: number } }>()
);

// ! Deprecated: change action for registerSignatureSuccess
export const createSimpleSignatureSuccess = createAction(
  ESignatureActions.createSimpleSignatureSuccess,
  props<{ payload: DocumentSignature }>()
);

// ! Deprecated: change action for registerSignatureError
export const createSimpleSignatureError = createAction(
  ESignatureActions.createSimpleSignatureError,
  props<{ payload: HttpErrorResponse }>()
);

export const registerSignature = createAction(
  '[Signature] Register Signature',
  props<{
    payload: {
      documentId: number;
      signRequestId: number;
      signatureData: ICreateSignatureRequest;
      token?: string;
    };
  }>()
);

export const registerSignatureSuccess = createAction(
  '[Signature] Register Signature Success',
  props<{ payload: ISignatureResponse }>()
);

export const registerSignatureError = createAction(
  '[Signature] Register Signature Error',
  props<{ payload: HttpErrorResponse }>()
);

export const RejectSignature = createAction(
  '[Signature] Reject Signature',
  props<{
    payload: {
      documentId: number;
      signRequestId: number;
      rejectData?: IRejectSignatureRequest;
      token?: string;
    };
  }>()
);

export const RejectSignatureSuccess = createAction(
  '[Signature] Reject Signature Success',
  props<{ payload: IRejectSignatureResponse }>()
);

export const RejectSignatureError = createAction(
  '[Signature] Reject Signature Error',
  props<{ payload: HttpErrorResponse }>()
);

export const GetSignatureQuote = createAction(
  ESignatureActions.GetSignatureQuote,
  props<{ payload: string }>()
);

export const GetSignatureQuoteSuccess = createAction(
  ESignatureActions.GetSignatureQuoteSuccess,
  props<{ payload: ITemplates[] }>()
);

export const GetSignatureQuoteError = createAction(
  ESignatureActions.GetSignatureQuoteError
);

export const ClearSignatureQuotes = createAction(
  ESignatureActions.ClearSignatureQuotes
);
